import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { API } from "common/utils";
import { RootState } from "./rootReducer";

export type FileUpload = {
  guid: string;
  file: string;
  fileName: string;
};

export type FileUploadPayload = {
  fileName: string;
  file: string;
};

class FileUploadAPI extends API<FileUpload> {
  async uploadFile(payload: FileUploadPayload): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}fileUpload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return (await res.json()) as { guid: string };
  }

  async getSingleFileUploadViaFileEntryApi(guid: string): Promise<FileUpload> {
    const res = await fetch(`${this.baseUrl}fileEntry/${guid}/fileUpload`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return (await res.json()) as FileUpload;
  }
}

const fileUploadApi = new FileUploadAPI("FileUpload");

export const getSingleFileUploadViaFileEntryApi = createAsyncThunk<
  Promise<FileUpload>,
  { guid: string }
>("fileUpload/getSingle", async ({ guid }: { guid: string }) => {
  const fileUpload = await fileUploadApi.getSingleFileUploadViaFileEntryApi(
    guid
  );
  return fileUpload;
});

export const uploadFile = createAsyncThunk<FileUpload, FileUploadPayload>(
  "fileUpload/uploadFile",
  async (payload: FileUploadPayload) => {
    const response = await fileUploadApi.uploadFile(payload);
    return { guid: response.guid, ...payload };
  }
);

const fileUploadAdapter = createEntityAdapter<FileUpload>({
  // we need this because IDs are stored in a field other than `field.id`
  selectId: (fileUpload: FileUpload) => fileUpload.guid,
  // Keeps the array sorted by guid
  sortComparer: (a: FileUpload, b: FileUpload) => a.guid.localeCompare(b.guid),
});

export const {
  selectById: selectFileUploadById,
  selectAll: selectAllFileUploads,
} = fileUploadAdapter.getSelectors((state: RootState) => state.fileUploads);

const initialFileUploadState: EntityState<FileUpload> =
  fileUploadAdapter.getInitialState({});

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState: initialFileUploadState,
  reducers: {},
  extraReducers: {
    [getSingleFileUploadViaFileEntryApi.fulfilled.type]: (
      state,
      { payload }
    ) => {
      fileUploadAdapter.upsertOne(state, payload as unknown as FileUpload);
    },
    [uploadFile.fulfilled.type]: (state, { payload }) => {
      fileUploadAdapter.addOne(state, payload as unknown as FileUpload);
    },
  },
});

export const fileUploadReducer = fileUploadSlice.reducer;
